// MeuClube.js — aba "Clube Insigne" do perfil.
//
// ================= POR QUE ESTA ABA FOI RECONSTRUÍDA =================
//
// O bloco "Continue lendo" morava aqui, alimentado por `GET /minhas-leituras`.
// Ele desceu para a aba de E-books, e a chamada saiu junto: desde que a estante
// passou a entrar por "comprei OU li 10%", a aba de E-books JÁ É a lista de
// livros com progresso. A mesma lista em dois lugares, com regras de entrada
// diferentes, diverge — é só questão de tempo, e no dia em que divergir ninguém
// vai saber qual das duas está certa.
//
// O que sobrava aqui depois dessa saída era um cartão com uma data. A aba foi
// remontada em volta de três perguntas, nesta ordem de importância:
//
//   1. QUANTO TEMPO AINDA TENHO. O dado já existia, escondido numa linha de
//      texto cinza. É ele que decide se a pessoa renova, então virou o número
//      grande da tela — e muda de cor quando faltam 5 dias ou menos.
//   2. O QUE A ASSINATURA JÁ ME DEU. Livros começados pelo Clube e páginas
//      somadas, tirados de `GET /minha-estante`.
//   3. POR ONDE EU VOLTO. O livro mais recente com progresso, e o caminho de
//      renovar antes de vencer.
//
// Quem NÃO assina continua caindo no cartão de oferta refeito em 03/09/2026 —
// ele está mais abaixo, com os motivos de cada peça.
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Reveal from "../../ui/Reveal";
import CountUp from "../../ui/CountUp";
import useConfirmarGasto, { opcoesUsarTicket } from "../../ui/ConfirmarGasto";
import AcervoDoClube from "../../clube/AcervoDoClube";
import { message } from "../../utils/toast";

const API_URL = process.env.REACT_APP_API;
const main = process.env.REACT_APP_MAIN;

const fmtData = (iso) => {
  if (!iso) return "";
  const d = new Date(typeof iso === "string" ? iso.replace(" ", "T") : iso);
  return isNaN(d) ? "" : d.toLocaleDateString("pt-BR");
};

const reais = (centavos) => "R$ " + ((Number(centavos) || 0) / 100).toFixed(2).replace(".", ",");

// Sem centavos, para o preço poder aparecer grande sem virar "R$ 50,00" — que
// ocupa mais espaço e comunica menos. Só quando o valor é redondo.
const reaisCurto = (centavos) => {
  const v = (Number(centavos) || 0) / 100;
  return Number.isInteger(v) ? `R$ ${v}` : reais(centavos);
};

const cabecalho = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

// ================= AS MEDIDAS DOS BOTÕES MORAM AQUI =================
//
// `.btn-solido` (App.css) entrega fundo, borda tracejada e a inversão do hover
// — e mais nada. Sem padding, sem raio, sem peso de fonte. O botão desta aba
// vinha com `className="btn-solido inline-flex"` e só: um retângulo laranja
// colado no próprio texto, que é o defeito relatado pelo cliente.
//
// São duas classes, e não uma, porque a tela precisa de HIERARQUIA: uma ação
// principal que se vê de longe e uma subordinada que continua alcançável. Dois
// botões sólidos lado a lado não são uma escolha, são um empate — e no empate
// a pessoa não clica em nenhum dos dois.
const BOTAO_PRINCIPAL =
  "btn-solido inline-flex items-center justify-center gap-2 rounded-lg px-5 py-2.5 text-sm font-bold";
const BOTAO_SECUNDARIO =
  "foco-laranja inline-flex items-center justify-center gap-2 rounded-lg border border-white/15 " +
  "bg-white/5 px-5 py-2.5 text-sm font-semibold text-white/85 transition-colors " +
  "hover:bg-white/10 hover:border-white/30";

// O BOTÃO DO TICKET É VERMELHO INTEIRO (pedido do dono, 11/09/2026).
//
// Ele já teve a cor só numa borda e num numerozinho, e assim continuava lendo
// como botão secundário cinza com um detalhe colorido. O vermelho é a cor do
// Ticket em todo o site — os cartões da Loja de Créditos, o selo do perfil, a
// tarja dos pacotes — e aqui ele diz, antes de qualquer texto, COM O QUE se
// renova. Fica ao lado do laranja da leitura sem competir: são ações
// diferentes, e agora parecem diferentes.
const BOTAO_TICKET =
  "foco-laranja inline-flex items-center justify-center gap-2 rounded-lg bg-insigne-ticket " +
  "px-5 py-2.5 text-sm font-bold text-insigne-ticket-contraste shadow-[0_4px_16px_rgb(var(--c-ticket)/0.35)] " +
  "transition-opacity hover:opacity-90";

// Um número da vitrine da assinatura. O `CountUp` entra SÓ aqui: número que
// celebra pode contar, número que avisa não. Ver a contagem regressiva de dias
// mais abaixo — animar "3 dias restantes" transforma um alerta em enfeite.
const TINTA_METRICA = {
  orange: {
    caixa: "border-insigne-orange/25 from-insigne-orange/[0.10]",
    chip: "bg-insigne-orange/15 text-insigne-orange",
    numero: "text-white",
  },
  ticket: {
    caixa: "border-insigne-ticket/25 from-insigne-ticket/[0.10]",
    chip: "bg-insigne-ticket/15 text-insigne-ticket",
    numero: "text-insigne-ticket",
  },
};

/**
 * Um número da vitrine da assinatura.
 *
 * O ícone mora num quadrado colorido, e não solto no canto: solto, ele era um
 * caractere cinza de 14px perdido acima de um número de 36 — o cartão inteiro
 * lia como um extrato de banco. O `CountUp` entra SÓ aqui: número que celebra
 * pode contar, número que avisa não (ver a contagem regressiva de dias).
 */
const Metrica = ({ icone, valor, rotulo, nota, cor = "orange", sufixo }) => {
  const tinta = TINTA_METRICA[cor] || TINTA_METRICA.orange;
  return (
    <div className={"rounded-2xl border bg-gradient-to-br via-insigne-navy-soft to-insigne-navy-soft " +
                    "p-4 md:p-5 " + tinta.caixa}>
      <span className={"inline-flex h-9 w-9 items-center justify-center rounded-lg text-base " + tinta.chip}>
        <i className={"bi " + icone} />
      </span>
      <p className={"mt-3 flex items-baseline gap-1.5 " + tinta.numero}>
        <CountUp to={valor} className="text-3xl md:text-4xl font-extrabold leading-none" />
        {sufixo && <span className="text-sm font-semibold text-white/45">{sufixo}</span>}
      </p>
      <p className="text-sm text-white/70 mt-1.5 leading-snug">{rotulo}</p>
      {nota && <p className="text-xs text-white/35 mt-1 leading-snug">{nota}</p>}
    </div>
  );
};

const MeuClube = () => {
  const [status, setStatus] = useState(null);
  // Capas só para quem AINDA NÃO assina: elas existem para vender. Buscar para
  // quem já paga seria uma requisição por visita sem nenhum uso.
  const [capas, setCapas] = useState([]);
  // A estante é o oposto: só serve a quem JÁ assina. `null` é "ainda não
  // voltou" e `[]` é "voltou vazia" — é essa diferença que decide se a tela
  // mostra o esqueleto do botão ou o botão pronto.
  const [estante, setEstante] = useState(null);
  const [estanteFalhou, setEstanteFalhou] = useState(false);
  const [carregando, setCarregando] = useState(true);
  const [usando, setUsando] = useState(false);
  // "meu" | "acervo" — a sub-aba de quem assina (pedido do dono, 11/09/2026).
  const [sub, setSub] = useState("meu");
  const { confirmar, dialogo } = useConfirmarGasto();

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const r = await fetch(`${API_URL}clube/status`, { headers: cabecalho() });
      if (r.ok) setStatus(await r.json());
    } catch (_) {
      /* a tela degrada para o convite de assinar */
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  // Depois do status, e só se a pessoa não assina.
  useEffect(() => {
    if (!status || status.ativa) return;
    let vivo = true;
    (async () => {
      try {
        const r = await fetch(`${API_URL}ebooks-recentes`);
        if (!r.ok) return;
        const data = await r.json();
        if (vivo && Array.isArray(data)) setCapas(data.filter((l) => l?.image).slice(0, 6));
      } catch (_) {
        // O card funciona sem capas. Falhar aqui não pode tirar a oferta do ar.
      }
    })();
    return () => { vivo = false; };
  }, [status]);

  // A ESTANTE VEM DEPOIS DO STATUS, DE PROPÓSITO, E NUNCA JUNTO COM ELE.
  //
  // Ela traz o dado de vitrine ("7 livros, 812 páginas") e o atalho de volta à
  // leitura. O status é o que responde se a assinatura ainda vale — a única
  // informação que a pessoa veio buscar. Pedir as duas em paralelo faria a
  // primeira pintura esperar a mais lenta; pior, uma estante que falha levaria
  // junto a resposta sobre a assinatura.
  //
  // Por isso a falha aqui é silenciosa: `estanteFalhou` apaga os números e o
  // atalho de retomar, e o cartão da assinatura continua inteiro.
  useEffect(() => {
    if (!status?.ativa) return;
    let vivo = true;
    (async () => {
      try {
        const r = await fetch(`${API_URL}minha-estante`, { headers: cabecalho() });
        if (!r.ok) throw new Error("estante indisponível");
        const d = await r.json();
        if (vivo) setEstante(Array.isArray(d?.livros) ? d.livros : []);
      } catch (_) {
        if (vivo) setEstanteFalhou(true);
      }
    })();
    return () => { vivo = false; };
  }, [status]);

  const ativo = !!status?.ativa;
  // Aviso na tela quando falta pouco. O e-mail também avisa, mas depende de
  // a pessoa abrir o e-mail — e de o cron do servidor existir.
  const dias = Number(status?.dias_restantes) || 0;
  const acabando = ativo && dias <= 5;

  const livros = estante || [];
  // Só o que entrou pelo Clube: o livro comprado a pessoa teria de qualquer
  // jeito, e contá-lo aqui daria à assinatura um mérito que não é dela.
  const doClube = livros.filter((l) => l?.origem === "clube");
  // Soma de `ultima_pagina`, ou seja: a marca mais distante de cada livro. Não
  // é contagem de páginas viradas — o servidor não guarda isso, e fazer a conta
  // de outro jeito aqui seria inventar o número. A nota sob o dado diz na tela
  // exatamente o que ele é.
  const paginas = doClube.reduce((soma, l) => soma + (Number(l?.progresso?.ultima_pagina) || 0), 0);
  // UM livro, o mais recente — a estante já chega ordenada por leitura recente.
  // Não é uma lista: a lista mora na aba de E-books agora, e repeti-la aqui
  // recriaria a duplicação que a migração acabou de desfazer.
  const retomar = livros.find((l) => l?.pode_ler && l?.progresso) || null;
  const esperandoEstante = ativo && !estante && !estanteFalhou;

  // A chave da aba continua sendo "meus-livros" mesmo com o rótulo "E-books":
  // renomeá-la quebraria os atalhos legados em silêncio (ver Perfil.js).
  const irParaEbooks = () =>
    window.dispatchEvent(new CustomEvent("changePerfiltab", { detail: "meus-livros" }));

  // ================= POR QUE A ORDEM DOS BOTÕES INVERTE =================
  //
  // Em dia normal, a ação principal é VOLTAR A LER: quem tem 26 dias pela
  // frente não precisa ser cobrado de novo por algo que já pagou, e é a leitura
  // que faz a renovação acontecer no fim do mês.
  //
  // Faltando 5 dias ou menos, os dois trocam de lugar. A ação que PRESERVA o
  // acesso passa a valer mais do que a que o usa: quem clica em "continuar
  // lendo" hoje e não renova perde o livro na semana que vem, no meio da
  // leitura. E renovar antes do fim não custa dia nenhum — eles são somados,
  // não substituídos.
  // ===== RENOVAR MORA AQUI (11/09/2026) =====
  //
  // "Renovar" levava à página do Clube — e a página do Clube, para quem já
  // assina, passou a mandar para cá ("Meu Clube"). As duas se apontavam, e não
  // havia onde renovar. Agora o Ticket é usado AQUI, depois da caixa de
  // confirmação (ui/ConfirmarGasto), que mostra a data nova antes do clique.
  // Sem Ticket guardado, o botão leva à Loja de Créditos, que é onde se compra.
  const temTicket = Number(status?.saldo_tickets) > 0;
  const diasPorTicket = Number(status?.dias_por_ticket) || Number(status?.dias) || 30;

  const usarTicket = async () => {
    if (usando) return;
    const ok = await confirmar(opcoesUsarTicket({
      saldoTickets: status?.saldo_tickets,
      dias: diasPorTicket,
      fim: status?.fim,
      ativa: ativo,
    }));
    if (!ok) return;
    setUsando(true);
    try {
      const r = await fetch(`${API_URL}creditos/usar-no-clube`, {
        method: "POST",
        headers: { ...cabecalho(), "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ quantidade: 1 }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d?.error || d?.message || "Não foi possível usar o Ticket agora.");
      message.success(`Clube ativo por mais ${d.dias_restantes} dias.`);
      await carregar();
      // O cabeçalho do perfil mostra o saldo de Tickets: um a menos agora.
      window.dispatchEvent(new CustomEvent("creditosAtualizados"));
    } catch (e) {
      message.error(e.message);
    } finally {
      setUsando(false);
    }
  };

  const irParaLoja = () =>
    window.dispatchEvent(new CustomEvent("changePerfiltab", { detail: "creditos" }));

  // O BOTÃO DIZ O QUE ELE GASTA, e quanto a pessoa tem (pedido de 11/09/2026).
  // "Renovar antes de vencer" escondia as duas coisas que decidem o clique: que
  // renovar custa um Ticket, e que ela tem Tickets guardados. A frase sobre os
  // dias somarem vai logo abaixo dos botões — é o que tira o medo de renovar
  // cedo e perder o que já foi pago.
  const acaoRenovar = temTicket ? (
    <button
      type="button"
      onClick={usarTicket}
      disabled={usando}
      className={BOTAO_TICKET + " disabled:opacity-60"}
    >
      <i className={"bi " + (usando ? "bi-arrow-repeat animate-spin" : "bi-ticket-perforated-fill")} />
      Usar Tickets
      {/* O saldo dentro do botão, é o número que decide o clique — mas SEM
          pastilha em volta (pedido do dono, 11/09/2026): um círculo claro sobre
          o vermelho lia como um segundo botão dentro do botão. Separado por um
          ponto, ele continua sendo lido como parte do rótulo. */}
      <span className="font-extrabold text-paper/80">· {status.saldo_tickets}</span>
    </button>
  ) : (
    <button type="button" onClick={irParaLoja} className={BOTAO_TICKET}>
      <i className="bi bi-bag-plus" /> Comprar Ticket
    </button>
  );

  // A linha que explica o Ticket, embaixo dos dois botões de quem já assina.
  // A explicação do Ticket numa FAIXA, na cor dele: solta como frase cinza de
  // 12px, ela desaparecia debaixo de dois botões — e é ela que tira o medo de
  // renovar cedo ("perco os dias que já paguei?").
  const notaDosTickets = temTicket ? (
    <div className="relative mt-4 flex items-start gap-2.5 rounded-xl border border-insigne-ticket/25
                    bg-insigne-ticket/[0.08] px-4 py-3">
      <i className="bi bi-ticket-perforated-fill mt-0.5 shrink-0 text-insigne-ticket" />
      <p className="text-xs leading-relaxed text-white/60">
        Você tem{" "}
        <strong className="text-insigne-ticket">
          {status.saldo_tickets} {Number(status.saldo_tickets) === 1 ? "Ticket" : "Tickets"}
        </strong>{" "}
        guardados, que valem{" "}
        <strong className="text-white/80">{status.saldo_tickets * diasPorTicket} dias</strong> de
        Clube. Os dias <strong className="text-white/80">somam</strong> ao prazo atual: usar agora
        não faz você perder nada do que já pagou.
      </p>
    </div>
  ) : null;

  // O esqueleto existe pelo mesmo motivo do esqueleto do preço, lá embaixo: um
  // botão que aparece escrito "Escolher um livro" e vira "Continuar lendo" meio
  // segundo depois é pior do que um botão que chega pronto — a pessoa já moveu
  // o cursor para o rótulo que leu primeiro. "Renovar" não espera por nada
  // disso: a ação que salva o acesso não pode depender de uma chamada que só
  // serve à vitrine.
  const acaoLeitura = esperandoEstante ? (
    <span className="inline-block h-[42px] w-44 rounded-lg bg-white/10 animate-pulse" aria-hidden="true" />
  ) : (
    // "Escolher um livro" vai para o ACERVO DO CLUBE (a grade dentro da página
    // do Clube, com busca e categorias), e não para /livros — pedido do dono em
    // 11/09/2026. /livros é a vitrine de COMPRA: manda quem já paga para uma
    // página cheia de preço e carrinho.
    <Link
      to={retomar ? main + "ler/" + retomar.id : main + "clube#acervo"}
      className={acabando ? BOTAO_SECUNDARIO : BOTAO_PRINCIPAL}
    >
      <i className={"bi " + (retomar ? "bi-book-half" : "bi-compass")} />
      {retomar ? "Continuar lendo" : "Escolher um livro"}
    </Link>
  );

  return (
    <div>
      <h2 className="titulo-display text-[2.1rem] md:text-[2.5rem] flex items-center gap-2 mb-4">
        <i className="bi bi-stars" /> Clube Insigne
      </h2>

      {/* ===== AS DUAS SUB-ABAS DE QUEM ASSINA (pedido do dono, 11/09/2026) =====
          O acervo — a MESMA grade da página do Clube, com busca, categorias e
          paginação — passou a morar aqui também. Quem acabou de assinar entra
          no perfil para ver o que comprou; sem isto, o caminho até os 200
          livros era voltar ao site e achar a página do Clube de novo.
          É o MESMO componente daquela página, não uma cópia. */}
      {ativo && !carregando && (
        <div className="mb-5 flex w-max max-w-full gap-1.5 overflow-x-auto rounded-xl border border-line
                        bg-insigne-navy-deep/50 p-1">
          {[
            { chave: "meu", rotulo: "Minha assinatura", icone: "bi-patch-check-fill" },
            { chave: "acervo", rotulo: "Acervo do Clube", icone: "bi-collection-fill" },
          ].map((a) => (
            <button
              key={a.chave}
              type="button"
              onClick={() => setSub(a.chave)}
              aria-pressed={sub === a.chave}
              className={
                "foco-laranja inline-flex items-center gap-2 whitespace-nowrap rounded-lg px-3.5 py-2 " +
                "text-sm font-semibold transition-colors " +
                (sub === a.chave
                  ? "bg-insigne-orange text-paper"
                  : "text-white/65 hover:bg-white/5 hover:text-white")
              }
            >
              <i className={"bi " + a.icone} />
              {a.rotulo}
            </button>
          ))}
        </div>
      )}

      {ativo && sub === "acervo" && !carregando ? (
        <AcervoDoClube />
      ) : (
      <>

      {carregando ? (
        <p className="text-white/50 py-10 text-center">Carregando...</p>
      ) : (
        <>
          <Reveal>
            {ativo ? (
              /* ============ O CARTÃO DE QUEM JÁ ASSINA ============
                 Ele era uma PILHA: selo, número, data, livro, botões e nota,
                 tudo encostado na margem esquerda e tudo com o mesmo peso
                 depois do número. Em tela de computador isso deixava metade do
                 cartão vazia — e a metade cheia parecia um comprovante de
                 pagamento, que é exatamente o que esta aba não pode ser.

                 Agora são DUAS COLUNAS com funções diferentes: à esquerda o
                 estado da assinatura (a pergunta "ainda vale?"), à direita o
                 livro em andamento (a pergunta "e daí?"). A segunda é a que faz
                 a pessoa voltar, então ela deixou de ser um retângulo de 3mm no
                 meio da pilha e passou a ocupar metade do cartão.

                 O fundo virou gradiente pelo mesmo motivo do cartão da oferta,
                 logo abaixo: chapado, ele se confundia com os cartões vizinhos
                 da página. */
              <div
                className={
                  "relative rounded-2xl border p-5 md:p-6 overflow-hidden bg-gradient-to-br " +
                  (acabando
                    ? "border-amber-400/40 from-amber-400/[0.14] via-insigne-navy-soft to-insigne-navy-soft"
                    : "border-insigne-orange/40 from-insigne-orange/[0.14] via-insigne-navy-soft to-insigne-navy-soft")
                }
              >
                {/* ===== O ENFEITE DO FUNDO =====
                    Duas manchas de luz e um ícone gigante quase apagado, atrás
                    de tudo (`-z-0`, sem evento de ponteiro). O cartão era um
                    retângulo chapado com texto — o dono chamou de "simples e
                    feio", e estava certo: o que dá corpo a um cartão desse
                    tamanho é profundidade, não mais texto.

                    `blur-3xl` custa uma camada de composição e nada de layout;
                    e `overflow-hidden` no cartão corta o que passa da borda. */}
                <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
                  <div className={"absolute -top-24 -right-16 h-64 w-64 rounded-full blur-3xl " +
                                  (acabando ? "bg-amber-400/10" : "bg-insigne-orange/10")} />
                  <div className="absolute -bottom-28 -left-20 h-56 w-56 rounded-full bg-insigne-ticket/10 blur-3xl" />
                  <i className={"bi bi-stars absolute -right-6 bottom-2 text-[9rem] leading-none " +
                                (acabando ? "text-amber-400/[0.06]" : "text-insigne-orange/[0.06]")} />
                </div>

                <div className="relative flex flex-col lg:flex-row lg:items-stretch gap-6 lg:gap-8">
                  {/* ---------- coluna 1: o estado da assinatura ---------- */}
                  <div className="lg:w-[42%] lg:shrink-0">
                    <span
                      className={
                        "inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 " +
                        "text-[11px] font-bold uppercase tracking-wide " +
                        (acabando
                          ? "border-amber-400/40 bg-amber-400/15 text-amber-300"
                          : "border-insigne-orange/30 bg-insigne-orange/15 text-insigne-orange")
                      }
                    >
                      <i className={"bi " + (acabando ? "bi-clock-history" : "bi-patch-check-fill")} />
                      {acabando ? "Está acabando" : "Assinatura ativa"}
                    </span>

                    {/* O NÚMERO GRANDE. É a resposta da aba, e vivia no meio de
                        uma frase cinza de 12px, colado à data — que obriga o
                        leitor a subtrair duas datas para saber o que importa.

                        `dias_restantes` é ceil(horas/24): na última hora ele
                        chega a zero com a assinatura ainda válida, e "0 dias
                        restantes" embaixo de "assinatura ativa" é uma
                        contradição dentro da mesma caixa. Daí o caso separado.

                        NÃO HÁ BARRA DE CICLO aqui, e a ausência é decisão: ela
                        exigiria o início do período, e a renovação abre uma
                        linha nova com início=hoje e fim=fim antigo+30 dias. A
                        barra representaria um ciclo de até 60 dias e mentiria
                        sobre quanto já passou. */}
                    <div className="mt-4 flex flex-wrap items-end gap-x-3 gap-y-1">
                      {dias > 0 ? (
                        <>
                          <span
                            className={
                              "text-6xl md:text-7xl font-extrabold leading-[0.85] tracking-tight " +
                              (acabando ? "text-amber-300" : "text-white")
                            }
                          >
                            {dias}
                          </span>
                          <span className="text-white/70 text-sm md:text-base pb-1">
                            {dias === 1 ? "dia restante" : "dias restantes"}
                          </span>
                        </>
                      ) : (
                        <span
                          className={
                            "text-4xl md:text-5xl font-extrabold leading-none " +
                            (acabando ? "text-amber-300" : "text-white")
                          }
                        >
                          Último dia
                        </span>
                      )}
                    </div>

                    {/* A data num selo, e não numa frase cinza: ela é o
                        segundo dado mais consultado desta aba, e solta no meio
                        do texto ela tinha o mesmo peso do aviso de renovação
                        logo abaixo. */}
                    <p className="mt-4 inline-flex items-center gap-2 rounded-lg border border-white/15
                                  bg-white/[0.06] px-3 py-1.5 text-sm text-white/70">
                      <i className="bi bi-calendar-check text-white/45" />
                      Vale até <strong className="text-white/90">{fmtData(status.fim)}</strong>
                    </p>

                    <p className="text-xs text-white/45 mt-4 flex items-start gap-1.5 leading-relaxed">
                      <i className="bi bi-shield-check mt-0.5 shrink-0" />
                      <span>
                        Não renova sozinho. Nada é cobrado sem você pedir.
                        Renovando antes do fim, os dias são somados.
                      </span>
                    </p>
                  </div>

                  {/* ---------- coluna 2: o livro em andamento ----------
                      A linha divisória só existe no desktop: no celular as duas
                      colunas viram uma pilha e um traço horizontal ali só
                      cortaria o cartão em dois sem separar nada. */}
                  <div className="flex-1 min-w-0 lg:border-l lg:border-white/10 lg:pl-8">
                    {esperandoEstante ? (
                      <div className="flex gap-4">
                        <div className="w-20 h-28 rounded-lg bg-white/10 animate-pulse shrink-0" />
                        <div className="flex-1 space-y-2 pt-1">
                          <div className="h-3 w-24 rounded bg-white/10 animate-pulse" />
                          <div className="h-4 w-full rounded bg-white/10 animate-pulse" />
                          <div className="h-2 w-full rounded-selo bg-white/10 animate-pulse mt-3" />
                        </div>
                      </div>
                    ) : retomar ? (
                      /* O caminho de volta. É o que transforma esta aba de
                         comprovante de pagamento em porta de entrada da leitura.
                         UM só livro, de propósito — a lista mora na aba de
                         E-books, e repeti-la aqui recriaria a duplicação que a
                         migração acabou de desfazer.

                         A BARRA é a novidade que faltava: "página 56 de 158"
                         obriga a dividir de cabeça para saber se está no começo
                         ou no fim. O número já vem pronto do servidor
                         (`progresso.percentual`), então a tela não recalcula —
                         duas contas do mesmo dado divergem. */
                      <div>
                        <p className="text-[11px] uppercase tracking-wide font-bold text-white/40">
                          Você parou aqui
                        </p>
                        <div className="flex gap-4 mt-3">
                          <div className="w-20 h-28 md:w-[88px] md:h-[124px] rounded-lg overflow-hidden bg-insigne-navy-deep shrink-0 flex items-center justify-center border border-white/10 shadow-lg">
                            {retomar.image ? (
                              <img
                                src={`${API_URL}public/${retomar.image}`}
                                alt=""
                                loading="lazy"
                                className="w-full h-full object-cover"
                                onError={(e) => { e.target.style.display = "none"; }}
                              />
                            ) : (
                              <i className="bi bi-book text-white/30 text-xl" />
                            )}
                          </div>

                          <div className="min-w-0 flex-1 flex flex-col justify-center">
                            <p className="font-bold text-base leading-snug line-clamp-2">
                              {retomar.titulo}
                            </p>

                            {Number(retomar.progresso?.percentual) > 0 && (
                              <div className="mt-3">
                                <div className="h-1.5 rounded-selo bg-white/10 overflow-hidden">
                                  <div
                                    className={
                                      "h-full rounded-selo " +
                                      (acabando ? "bg-amber-300" : "bg-insigne-orange")
                                    }
                                    style={{ width: `${Math.min(100, retomar.progresso.percentual)}%` }}
                                  />
                                </div>
                                <p className="text-xs text-white/50 mt-1.5">
                                  <strong className="text-white/80">{retomar.progresso.percentual}%</strong>
                                  {" · "}
                                  página {retomar.progresso.ultima_pagina}
                                  {retomar.progresso.total_paginas > 0 && ` de ${retomar.progresso.total_paginas}`}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : (
                      /* Sem livro começado, esta coluna ficaria vazia e o
                         cartão voltaria a ter o buraco que ele veio corrigir.
                         No lugar entra o que a assinatura JÁ dá — que é o que
                         essa pessoa ainda não usou. */
                      <div className="h-full flex flex-col justify-center">
                        <i className="bi bi-collection text-2xl text-insigne-orange" />
                        <p className="text-lg font-bold mt-2 leading-snug">
                          {status?.acervo_total > 0
                            ? `${status.acervo_total} livros esperando você`
                            : "O acervo está liberado"}
                        </p>
                        <p className="text-sm text-white/55 mt-1 leading-relaxed">
                          Qualquer e-book do catálogo, no navegador, com marcador
                          de página, e continua de onde parou no celular.
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* A ordem no DOM acompanha a hierarquia, e não só o estilo:
                    quem navega por teclado ou por leitor de tela precisa chegar
                    primeiro na ação principal, seja ela qual for hoje. */}
                <div className="mt-6 pt-5 border-t border-white/10 flex flex-wrap items-center gap-3">
                  {acabando ? (
                    <>{acaoRenovar}{acaoLeitura}</>
                  ) : (
                    <>{acaoLeitura}{acaoRenovar}</>
                  )}
                  {livros.length > 0 && (
                    <button
                      type="button"
                      onClick={irParaEbooks}
                      className="foco-laranja link-sublinhado text-sm text-white/55 hover:text-white transition-colors"
                    >
                      Ver todos na aba E-books
                    </button>
                  )}
                </div>

                {notaDosTickets}
              </div>
            ) : (
              /* ============ A OFERTA DO CLUBE ============
                 O card anterior tinha o MESMO fundo dos cartões vizinhos, um
                 ícone pequeno, um parágrafo cinza e um botão de contorno escrito
                 "Conhecer o Clube". Ele não dizia o preço, não dizia quantos
                 livros, e não tinha imagem nenhuma. Lido de relance, parecia
                 aviso de sistema — e aviso a gente pula.

                 O que mudou, e o motivo de cada peça:
                   - fundo em gradiente laranja: ele PRECISA não ser mais um
                     cartão da página;
                   - o número de livros em tamanho de título: é o argumento mais
                     forte que existe, e estava faltando;
                   - o preço compara com um livro, porque quem está nesta tela
                     acabou de ver os preços do catálogo;
                   - capas de verdade, porque acervo se mostra;
                   - "Assinar por R$ 50" no lugar de "Conhecer": conhecer
                     convida a adiar;
                   - "não renova sozinha" fica à vista, porque é o medo que
                     trava a assinatura, e ele já era dito — só que apenas para
                     quem JÁ assinava. */
              <div className="rounded-2xl border border-insigne-orange/40 bg-gradient-to-br from-insigne-orange/[0.16] via-insigne-navy-soft to-insigne-navy-soft p-5 md:p-7 overflow-hidden">
                <p className="inline-flex items-center gap-1.5 rounded-md bg-insigne-orange/15 border border-insigne-orange/30 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-insigne-orange">
                  <i className="bi bi-stars" /> Clube Insigne
                </p>

                <h3 className="mt-3 titulo-display mb-0 text-[2.4rem] md:text-[2.9rem] text-white">
                  {status?.acervo_total > 0 ? (
                    <>Leia <span className="text-insigne-orange">os {status.acervo_total} livros</span> do acervo</>
                  ) : (
                    <>Leia <span className="text-insigne-orange">todo o acervo</span> pelo site</>
                  )}
                </h3>

                <p className="text-white/65 mt-2 text-sm leading-relaxed max-w-xl">
                  Qualquer e-book do catálogo, no navegador, com marcador de página
                  e anotações, e continua de onde parou, no computador e no celular.
                </p>

                {capas.length > 0 && (
                  /* As capas ficam levemente sobrepostas e cortadas na borda: dizem
                     "tem mais" sem precisar de um texto dizendo "e muitos outros". */
                  <div className="flex mt-5 -space-x-3" aria-hidden="true">
                    {capas.map((l) => (
                      <img
                        key={l.id}
                        src={`${API_URL}public/${l.image}`}
                        alt=""
                        loading="lazy"
                        className="w-14 h-20 md:w-16 md:h-24 rounded-md object-cover border-2 border-insigne-navy-soft shadow-lg"
                      />
                    ))}
                  </div>
                )}

                {/* SO MOSTRA PRECO QUANDO SABE O PRECO.
                    `status` comeca nulo e `clube/status` pode falhar sem retry;
                    `reaisCurto(undefined)` dava "R$ 0", e o botao logo abaixo
                    virava "Assinar por R$ 0" — uma oferta que parece defeito, ou
                    pior, uma promessa de gratuidade. */}
                {Number(status?.preco_centavos) > 0 ? (
                  <div className="mt-5 flex flex-wrap items-end gap-x-3 gap-y-1">
                    <span className="text-3xl font-extrabold text-white leading-none">
                      {reaisCurto(status.preco_centavos)}
                    </span>
                    <span className="text-white/60 text-sm">
                      por {status?.dias || 30} dias, menos que um livro
                    </span>
                  </div>
                ) : (
                  <div className="mt-5 h-9 w-40 rounded-lg bg-white/10 animate-pulse" />
                )}

                <div className="mt-5 flex flex-wrap items-center gap-3">
                  {/* Mesmas medidas do botão principal do outro ramo: é a mesma
                      posição na hierarquia, e duas medidas diferentes para o
                      mesmo papel é como a inconsistência entra de novo. */}
                  {/* O RÓTULO SEGUE O SALDO, como na página do Clube. Quem tem
                      Ticket guardado não precisa comprar nada — precisa saber
                      que pode entrar agora, e entra DAQUI: desde 11/09/2026 o
                      gasto tem a caixa de confirmação com a data nova, que era
                      o que antes só a página do Clube explicava. Sem Ticket, o
                      botão leva à página do Clube, que vende. */}
                  {temTicket ? (
                    <button
                      type="button"
                      onClick={usarTicket}
                      disabled={usando}
                      className={BOTAO_PRINCIPAL + " disabled:opacity-60"}
                    >
                      <i className={"bi " + (usando ? "bi-arrow-repeat animate-spin" : "bi-ticket-perforated-fill")} />
                      {`Usar 1 Ticket · ${diasPorTicket} dias`}
                    </button>
                  ) : (
                    <Link to={main + "clube"} className={BOTAO_PRINCIPAL}>
                      <i className="bi bi-stars" />
                      {Number(status?.preco_centavos) > 0
                        ? `Comprar Ticket · ${reaisCurto(status.preco_centavos)}`
                        : "Conhecer o Clube"}
                    </Link>
                  )}
                  {/* O "ou N Insigne Coins" saiu: desde 10/09/2026 coin não
                      compra Clube, compra TICKET — e o preço em coins do ticket
                      varia por pacote, então repeti-lo aqui seria mostrar um
                      número que a loja de créditos desmente. */}
                  {Number(status?.saldo_tickets) > 0 && (
                    <span className="text-sm text-white/55">
                      você tem{" "}
                      <strong className="text-insigne-ticket">
                        {status.saldo_tickets} {Number(status.saldo_tickets) === 1 ? "Ticket" : "Tickets"}
                      </strong>
                    </span>
                  )}
                </div>

                {Number(status?.assinantes) > 0 && (
                  <p className="text-xs text-white/50 mt-4 flex items-start gap-1.5 leading-relaxed">
                    <i className="bi bi-people-fill mt-0.5 text-insigne-orange" />
                    {status.assinantes} pessoas já leem pelo Clube.
                  </p>
                )}

                <p className="text-xs text-white/45 mt-2 flex items-start gap-1.5 leading-relaxed">
                  <i className="bi bi-shield-check mt-0.5" />
                  Não renova sozinha. Nada é cobrado sem você pedir.
                </p>
              </div>
            )}
          </Reveal>

          {/* ---------- o que a assinatura já rendeu ----------
              Só aparece com pelo menos um livro começado. Um placar zerado não
              é vitrine: é cobrança, e cobra justamente quem acabou de pagar. */}
          {ativo && doClube.length > 0 && (
            <Reveal className="mt-8">
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                <i className="bi bi-graph-up-arrow" /> O que o Clube já te deu
              </h3>
              <div className={"grid gap-3 md:gap-4 " + (temTicket ? "grid-cols-2 lg:grid-cols-3" : "grid-cols-2")}>
                {/* "começados", e não "lidos": o critério do servidor para o
                    livro entrar na estante é 10% de leitura. Escrever "lidos"
                    seria a tela prometendo mais do que o dado sustenta. */}
                <Metrica
                  icone="bi-journal-bookmark"
                  valor={doClube.length}
                  rotulo={doClube.length === 1 ? "livro começado pelo Clube" : "livros começados pelo Clube"}
                  nota={status?.acervo_total > 0 ? `de ${status.acervo_total} no acervo` : null}
                />
                <Metrica
                  icone="bi-file-earmark-text"
                  valor={paginas}
                  rotulo={paginas === 1 ? "página lida" : "páginas lidas"}
                  nota="somando onde você parou em cada livro"
                />
                {/* O TERCEIRO NÚMERO É O SALDO, e ele fecha a conta que os
                    outros dois abrem: o que já foi lido, e por quanto tempo
                    ainda dá para ler. Só aparece com Ticket guardado — zerado,
                    seria cobrança de quem acabou de pagar. */}
                {temTicket && (
                  <Metrica
                    icone="bi-ticket-perforated-fill"
                    cor="ticket"
                    valor={Number(status.saldo_tickets) || 0}
                    sufixo={Number(status.saldo_tickets) === 1 ? "Ticket" : "Tickets"}
                    rotulo={`${(Number(status.saldo_tickets) || 0) * diasPorTicket} dias guardados`}
                    nota="usados quando você quiser, sem renovar sozinho"
                  />
                )}
              </div>

              {/* AS CAPAS, e não só os números. Dois algarismos num retângulo
                  são um extrato; as capas são a memória do que a pessoa leu —
                  e é ela que faz a assinatura parecer ter valido a pena na
                  hora de renovar. É o mesmo argumento que o cartão da oferta
                  usa para quem ainda não assina: acervo se mostra.

                  Clicáveis, porque uma capa que não abre o livro é enfeite. */}
              {doClube.some((l) => l.image) && (
                <div className="mt-4 flex flex-wrap gap-2.5">
                  {doClube.filter((l) => l.image).slice(0, 8).map((l) => (
                    <Link
                      key={l.id}
                      to={l.pode_ler ? main + "ler/" + l.id : main + "livros/" + l.id}
                      title={l.titulo}
                      className="foco-laranja group relative w-12 h-[68px] rounded-md overflow-hidden
                                 border border-white/10 shadow-md transition-transform hover:-translate-y-1"
                    >
                      <img
                        src={`${API_URL}public/${l.image}`}
                        alt={l.titulo}
                        loading="lazy"
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.style.display = "none"; }}
                      />
                    </Link>
                  ))}
                </div>
              )}
            </Reveal>
          )}
        </>
      )}

      {/* A caixa de confirmação do gasto de Ticket. SEM ESTA LINHA o
          `confirmar()` abre uma promessa que ninguém resolve, e o botão
          "Usar Tickets" não faz nada — foi o que aconteceu entre 11/09/2026 e
          a correção no mesmo dia. */}
      </>
      )}

      {dialogo}
    </div>
  );
};

export default MeuClube;
